import { Button, DatePicker, Popover } from 'antd';
import AppPagination from 'components/common/footer/AppPagination';
import StaffLeaveCalendarFooter from 'components/common/footer/staff_leave_calendat';
import dayjs, { Dayjs } from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { useState } from 'react';
import styled from 'styled-components';
import { absenceRange, workshiftPolicies } from 'utils';
import TwoSide from './TwoSide';
dayjs.extend(localizedFormat);

type tableHeader = {
  week: string;
  name_day: string;
  day: number;
  month: number;
  numberDateInWeek: number;
};

type StaffLeaveTableType = {
  state?: {
    month: Number;
    year: Number;
    page: Number;
  };
  headerData: tableHeader[];
  tableData: {
    id: number;
    user_name: string;
    publicHoliday: {
      day: string;
      month: string;
      period: string;
    }[];
    dateRange: absenceRange[];
    workshiftPolicies: workshiftPolicies[];
    absence_requests: {
      name: string;
      color: string;
      code: string;
      font_color: string;
    };
  }[];
  onPageChange: (p: number) => void;
  onMonthChange: (date: Date | Dayjs) => void;
  onYearChange: (date: Date | Dayjs) => void;
  onToday: () => void;
  totalPage: number;
};

const StaffLeaveCalendarTables = (props: StaffLeaveTableType) => {
  const {
    onMonthChange,
    onYearChange,
    headerData,
    tableData,
    totalPage,
    onToday,
    onPageChange,
    state,
  } = props;

  const [mouseHover, setMouseHover] = useState(null);

  const absenceType = tableData
    .flatMap((i) => i['absenceType'])
    .filter((a, b, c) => c.findIndex((a2) => a2.code === a.code) === b);

  const isGetTobeApprove = tableData.some((item) =>
    item.dateRange.some((i) => i.status === 'to_be_approved'),
  );

  return (
    <LeaveAndAbsenceTable>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: '0.5px solid #73a1e7',
          paddingLeft: 40,
        }}
      >
        <Button
          type={'text'}
          style={{
            fontWeight: 500,
            fontSize: 16,
          }}
          onClick={onToday}
        >
          Today
        </Button>

        <DatePicker
          className={'staff_leave_calendar_date_picker'}
          onChange={(d) => onMonthChange(d)}
          picker="month"
          format={'MMMM'}
          value={dayjs(`${state.year}/${state.month}`)}
          allowClear={false}
          defaultValue={dayjs()}
          style={{
            fontWeight: 500,
            fontSize: 16,
            width: 'fit-content',
          }}
          suffixIcon={
            <img
              src={'/icons/arrow_select.svg'}
              alt={'icon'}
              width={8}
              height={8}
            />
          }
          onPanelChange={(v) => onYearChange(v)}
        />
      </div>
      <table
        className="staffLeave_table thin-scroll"
        style={{
          color: '#000000',
          maxHeight: '435px',
          display: 'block',
          overflowY: 'auto',
        }}
      >
        <thead>
          <tr className="staff_leave-week">
            <th style={{ width: '10%' }} />
            {headerData?.map(
              (item, index: number) =>
                item.numberDateInWeek && (
                  <th
                    key={index}
                    colSpan={item.numberDateInWeek}
                    style={{ padding: '0 2px', width: 100, color: '#000000' }}
                  >
                    <div className="staff_leave_calendar_weeks">
                      Week {item.week}
                    </div>
                  </th>
                ),
            )}
          </tr>
          <tr>
            <th style={{ width: '10%' }} />

            {headerData.map((item: tableHeader, index) => (
              <th
                key={index}
                style={{
                  padding: '10px 5px',
                  textAlign: 'center',
                  width: '40px',
                  color: '#000000',
                }}
              >
                <span className="staff_leave_text">{item.name_day}</span>
              </th>
            ))}
          </tr>{' '}
          <HeaderRow>
            <th style={{ width: '10%' }} />

            {headerData.map((item: tableHeader, index) => (
              <th
                key={index}
                style={{
                  textAlign: 'center',
                  color: '#000000',
                }}
              >
                <span
                  className="staff_leave_text"
                  style={{
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '35px',
                    borderRadius:
                      (+item.day === new Date().getDate() &&
                        +item.month === new Date().getMonth() + 1) ||
                      mouseHover?.row === index
                        ? '10px'
                        : null,
                    background:
                      mouseHover?.row === index
                        ? '#FFE4E4'
                        : +item.day === new Date().getDate() &&
                          +item.month === new Date().getMonth() + 1
                        ? '#407FDD'
                        : null,
                    color:
                      +item.day === new Date().getDate() &&
                      +item.month === new Date().getMonth() + 1
                        ? '#fff'
                        : null,
                    transition: 'ease-in-out 300ms all',
                  }}
                >
                  {item.day}
                </span>
              </th>
            ))}
          </HeaderRow>
        </thead>

        <tbody style={{ marginTop: '20px' }}>
          {tableData &&
            tableData.map((item) => {
              const holiday = item?.publicHoliday;
              const workshift = item?.workshiftPolicies;

              return (
                <tr key={item.id} className="staffLeave_hover">
                  <td
                    className={'staff_leave_calendar_name_user'}
                    style={{
                      background: mouseHover?.column === item.id && '#FFE4E4',
                      transition: 'ease-in-out 300ms all',
                      verticalAlign: 'middle',
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      maxWidth: 250,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      width: 250,
                    }}
                  >
                    {item.user_name}
                  </td>
                  {item.dateRange.map((item2: absenceRange, index2: number) => {
                    const absencePopoverContent = (
                      <ul>
                        <li>
                          {dayjs(item2.full_day)
                            .format('LLLL')
                            .replace('12:00 AM', '')}
                        </li>
                        <li>{item.user_name}</li>
                        <li>'{item2['type_name']}'</li>
                        <li style={{ textTransform: 'capitalize' }}>
                          {item2['start_date']}-{item2['end_date']}(
                          {item2['time_range']}d){' '}
                          {!Array.isArray(item2.status) &&
                            item2.status?.replaceAll('_', ' ')}
                        </li>
                      </ul>
                    );

                    const allDayHoliday = holiday?.find(
                      (i) =>
                        +i.day === +item2.day &&
                        +i.month === +item2.month &&
                        i.period === 'all_day',
                    );
                    const holidayLeft = holiday?.find(
                      (i) =>
                        +i.day === +item2.day &&
                        +i.month === +item2.month &&
                        i.period === 'morning',
                    );
                    const holidayRight = holiday?.find(
                      (i) =>
                        +i.day === +item2.day &&
                        +i.month === +item2.month &&
                        i.period === 'afternoon',
                    );

                    // workshift
                    const restAll = workshift?.find(
                      (w) =>
                        w['restAll'] &&
                        w['day_name'] === item2.name_day.toLowerCase(),
                    );

                    const restRight = workshift?.find(
                      (w) =>
                        w['restRight'] &&
                        w['day_name'] === item2.name_day.toLowerCase(),
                    );

                    const restLeft = workshift?.find(
                      (w) =>
                        w['restLeft'] &&
                        w['day_name'] === item2.name_day.toLowerCase(),
                    );

                    return (
                      <td
                        key={index2}
                        style={{
                          height: 40,
                          width: 40,
                          background: 'white',
                          borderRadius: '1px',
                          overflow: 'hidden',
                          borderLeft:
                            allDayHoliday ||
                            restAll ||
                            (item2.hasOwnProperty('color') &&
                              item2['key'] !== 'holiday_left')
                              ? 'none'
                              : '1px solid #eff7ff',
                          borderRight:
                            allDayHoliday ||
                            restAll ||
                            (item2.hasOwnProperty('color') &&
                              item2['key'] !== 'holiday_right')
                              ? 'none'
                              : '1px solid #eff7ff',
                          borderTop: '1px solid #eff7ff',
                          borderBottom: '1px solid #eff7ff',
                        }}
                      >
                        <>
                          {{
                            off_morning: (
                              <>
                                {item2['type_name'] !== null ? (
                                  <Popover
                                    content={absencePopoverContent}
                                    arrow={false}
                                    placement="bottomLeft"
                                    className="is-flex"
                                  >
                                    <div
                                      style={{
                                        background:
                                          item2.status === 'to_be_approved'
                                            ? '#ffef5f'
                                            : item2.color,
                                        height: 40,
                                        width: '50%',
                                        overflow: 'hidden',
                                        color:
                                          item2.text_color === item2.color
                                            ? '#333'
                                            : item2.text_color,
                                      }}
                                      onMouseEnter={() => {
                                        setMouseHover({
                                          row: index2,
                                          column: item.id,
                                        });
                                      }}
                                      onMouseLeave={() => {
                                        setMouseHover(null);
                                      }}
                                      className="has-text-centered pt-2"
                                    >
                                      {item2.code}
                                    </div>
                                    {restRight && (
                                      <span className="staff_leave_rest" />
                                    )}
                                  </Popover>
                                ) : (
                                  <div className="is-flex">
                                    <div
                                      style={{
                                        background:
                                          item2.status === 'to_be_approved'
                                            ? '#ffef5f'
                                            : item2.color === null
                                            ? '#1E87A1'
                                            : item2.color,
                                        height: 40,
                                        width: '50%',
                                      }}
                                    />
                                    {restRight && (
                                      <span className="staff_leave_rest" />
                                    )}
                                  </div>
                                )}
                              </>
                            ),
                            off_noon: (
                              <>
                                {item2['type_name'] === null ? (
                                  <div className="is-flex">
                                    {restLeft && (
                                      <span className="staff_leave_rest" />
                                    )}
                                    <div
                                      style={{
                                        background:
                                          item2.status === 'to_be_approved'
                                            ? '#ffef5f'
                                            : item2.color === null
                                            ? '#1E87A1'
                                            : item2.color,
                                        height: 40,
                                        width: '50%',

                                        borderRight: 'none',
                                        borderLeft: 'none',
                                        borderRadius: '1px',
                                      }}
                                      className="is-pulled-right"
                                    />
                                  </div>
                                ) : (
                                  <Popover
                                    content={absencePopoverContent}
                                    arrow={false}
                                    placement="bottomLeft"
                                    className="is-flex"
                                  >
                                    {restLeft && (
                                      <span className="staff_leave_rest" />
                                    )}
                                    <div
                                      style={{
                                        background:
                                          item2.status === 'to_be_approved'
                                            ? '#ffef5f'
                                            : item2.color,
                                        height: 40,
                                        width: '50%',
                                        overflow: 'hidden',
                                        color:
                                          item2.text_color === item2.color
                                            ? '#333'
                                            : item2.text_color,
                                      }}
                                      onMouseEnter={() => {
                                        setMouseHover({
                                          row: index2,
                                          column: item.id,
                                        });
                                      }}
                                      onMouseLeave={() => {
                                        setMouseHover(null);
                                      }}
                                      className="is-pulled-right has-text-centered pt-2"
                                    >
                                      {item2.code}
                                    </div>
                                  </Popover>
                                )}
                              </>
                            ),
                            off_all: (
                              <>
                                {item2['type_name'] === null ? (
                                  <div
                                    style={{
                                      background:
                                        item2.status === 'to_be_approved'
                                          ? '#ffef5f'
                                          : item2.color === null && '#1E87A1',
                                      height: 40,

                                      borderRight: 'none',
                                      borderRadius: '1px',
                                      borderLeft: 'none',
                                    }}
                                  />
                                ) : (
                                  <Popover
                                    arrow={false}
                                    content={absencePopoverContent}
                                    placement="bottomLeft"
                                  >
                                    <div
                                      style={{
                                        background:
                                          item2.status === 'to_be_approved'
                                            ? '#ffef5f'
                                            : item2.color,
                                        height: 40,

                                        borderRight: 'none',
                                        overflow: 'hidden',
                                        borderLeft: 'none',
                                        color:
                                          item2.text_color === item2.color
                                            ? '#333'
                                            : item2.text_color,
                                      }}
                                      onMouseEnter={() => {
                                        setMouseHover({
                                          row: index2,
                                          column: item.id,
                                        });
                                      }}
                                      onMouseLeave={() => {
                                        setMouseHover(null);
                                      }}
                                      className="has-text-centered pt-2"
                                    >
                                      {item2.code}
                                    </div>
                                  </Popover>
                                )}
                              </>
                            ),
                            off_morning_and_holiday: (
                              <div className="is-flex">
                                {item2['type_name'] === null ? (
                                  <div
                                    style={{
                                      background:
                                        item2.status === 'to_be_approved'
                                          ? '#ffef5f'
                                          : item2.color === null && '#1E87A1',
                                      height: 40,
                                      width: '50%',

                                      borderRadius: '1px',
                                      borderRight: 'none',
                                      borderLeft: 'none',
                                    }}
                                  />
                                ) : (
                                  <Popover
                                    content={absencePopoverContent}
                                    arrow={false}
                                    placement="bottomLeft"
                                  >
                                    <div
                                      style={{
                                        background:
                                          item2.status === 'to_be_approved'
                                            ? '#ffef5f'
                                            : item2.color,
                                        height: 40,
                                        width: '50%',
                                        overflow: 'hidden',

                                        borderRight: 'none',
                                        borderRadius: '1px',
                                        borderLeft: 'none',
                                        color: item2.text_color,
                                      }}
                                      className="has-text-centered pt-2"
                                      onMouseEnter={() => {
                                        setMouseHover({
                                          row: index2,
                                          column: item.id,
                                        });
                                      }}
                                      onMouseLeave={() => {
                                        setMouseHover(null);
                                      }}
                                    >
                                      {item2.code}
                                    </div>
                                  </Popover>
                                )}

                                <span
                                  className={'staff_leave_rest'}
                                  style={{
                                    borderLeft: '2px solid #eff7ff',
                                  }}
                                />
                              </div>
                            ),
                            off_noon_and_holiday: (
                              <div className="is-flex">
                                <span className={'staff_leave_rest'} />
                                {item2['type_name'] === null ? (
                                  <div
                                    style={{
                                      background:
                                        item2.status === 'to_be_approved'
                                          ? '#ffef5f'
                                          : item2.color === null && '#1E87A1',
                                      height: 40,
                                      width: '50%',

                                      borderRight: 'none',
                                      borderRadius: '1px',
                                      borderLeft: 'none',
                                    }}
                                  />
                                ) : (
                                  <Popover
                                    content={absencePopoverContent}
                                    arrow={false}
                                    placement="bottomLeft"
                                  >
                                    <div
                                      style={{
                                        background:
                                          item2.status === 'to_be_approved'
                                            ? '#ffef5f'
                                            : item2.color,
                                        height: 40,
                                        width: '50%',
                                        overflow: 'hidden',

                                        borderRight: 'none',
                                        borderLeft: 'none',
                                        color: item2.text_color,
                                        borderRadius: '1px',
                                      }}
                                      className="has-text-centered pt-2"
                                      onMouseEnter={() => {
                                        setMouseHover({
                                          row: index2,
                                          column: item.id,
                                        });
                                      }}
                                      onMouseLeave={() => {
                                        setMouseHover(null);
                                      }}
                                    >
                                      {item2.code}
                                    </div>
                                  </Popover>
                                )}
                              </div>
                            ),
                            full_holiday: (
                              <div
                                style={{
                                  background: '#C6C6C6',
                                  height: 40,
                                  width: '100%',
                                  borderRadius: '1px',
                                }}
                              />
                            ),
                            midday_left: (
                              <>
                                {item2['type_name'] === null ? (
                                  <div
                                    style={{
                                      background:
                                        item2.status === 'to_be_approved'
                                          ? '#ffef5f'
                                          : item2.color === null && '#1E87A1',
                                      height: 40,
                                      width: '50%',

                                      borderRight: 'none',
                                      borderLeft: 'none',
                                      borderRadius: '1px',
                                    }}
                                  />
                                ) : (
                                  <Popover
                                    content={absencePopoverContent}
                                    arrow={false}
                                    placement="bottomLeft"
                                  >
                                    <div
                                      style={{
                                        background:
                                          item2.status === 'to_be_approved'
                                            ? '#ffef5f'
                                            : item2.color,
                                        height: 40,
                                        width: '50%',
                                        overflow: 'hidden',

                                        borderRight: 'none',
                                        borderLeft: 'none',
                                        color: item2.text_color,
                                      }}
                                      className="has-text-centered pt-2"
                                      onMouseEnter={() => {
                                        setMouseHover({
                                          row: index2,
                                          column: item.id,
                                        });
                                      }}
                                      onMouseLeave={() => {
                                        setMouseHover(null);
                                      }}
                                    >
                                      {item2.code}
                                    </div>
                                  </Popover>
                                )}
                              </>
                            ),
                            two_side: (
                              <TwoSide
                                item2={item2}
                                item={item}
                                index2={index2}
                                setMouseHover={setMouseHover}
                              />
                            ),
                            holiday_left: (
                              <div className="is-flex">
                                <span
                                  className={'staff_leave_rest'}
                                  style={{
                                    borderLeft: '1px solid #eff7ff',
                                  }}
                                />

                                {item2['type_name'] === null ? (
                                  <div
                                    style={{
                                      background:
                                        item2.status === 'to_be_approved'
                                          ? '#ffef5f'
                                          : item2.color === null && '#1E87A1',
                                      height: 40,
                                      width: '50%',

                                      borderRight: 'none',
                                      borderRadius: '1px',
                                      borderLeft: 'none',
                                    }}
                                  />
                                ) : (
                                  <Popover
                                    content={absencePopoverContent}
                                    arrow={false}
                                    placement="bottomLeft"
                                  >
                                    <div
                                      style={{
                                        background:
                                          item2.status === 'to_be_approved'
                                            ? '#ffef5f'
                                            : item2.color,
                                        height: 40,
                                        width: '50%',
                                        borderRight: 'none',
                                        borderLeft: 'none',
                                        color: item2.text_color,
                                        borderRadius: '1px',
                                        overflow: 'hidden',
                                      }}
                                      className="has-text-centered pt-2"
                                      onMouseEnter={() => {
                                        setMouseHover({
                                          row: index2,
                                          column: item.id,
                                        });
                                      }}
                                      onMouseLeave={() => {
                                        setMouseHover(null);
                                      }}
                                    >
                                      {item2.code}
                                    </div>
                                  </Popover>
                                )}
                              </div>
                            ),
                            holiday_right: (
                              <div className="is-flex">
                                {item2['type_name'] === null ? (
                                  <div
                                    style={{
                                      background:
                                        item2.status === 'to_be_approved'
                                          ? '#ffef5f'
                                          : item2.color === null && '#1E87A1',
                                      height: 40,
                                      width: '50%',

                                      borderRight: 'none',
                                      borderRadius: '1px',
                                      borderLeft: 'none',
                                    }}
                                  />
                                ) : (
                                  <Popover
                                    content={absencePopoverContent}
                                    arrow={false}
                                    placement="bottomLeft"
                                  >
                                    <div
                                      style={{
                                        background:
                                          item2.status === 'to_be_approved'
                                            ? '#ffef5f'
                                            : item2.color,
                                        height: 40,
                                        width: '50%',
                                        overflow: 'hidden',
                                        borderRight: 'none',
                                        borderLeft: 'none',
                                        color: item2.text_color,
                                        borderRadius: '1px',
                                      }}
                                      className="has-text-centered pt-2"
                                      onMouseEnter={() => {
                                        setMouseHover({
                                          row: index2,
                                          column: item.id,
                                        });
                                      }}
                                      onMouseLeave={() => {
                                        setMouseHover(null);
                                      }}
                                    >
                                      {item2.code}
                                    </div>
                                  </Popover>
                                )}
                                <span
                                  className={'staff_leave_rest'}
                                  style={{
                                    borderRight: '1px solid #eff7ff',
                                  }}
                                />
                              </div>
                            ),
                            rest_right: (
                              <div className="is-flex">
                                {item2['type_name'] === null ? (
                                  <div
                                    style={{
                                      background:
                                        item2.status === 'to_be_approved'
                                          ? '#ffef5f'
                                          : item2.color === null && '#1E87A1',
                                      height: 40,
                                      width: '50%',
                                      borderRadius: '1px',
                                    }}
                                  />
                                ) : (
                                  <Popover
                                    content={absencePopoverContent}
                                    arrow={false}
                                    placement="bottomLeft"
                                  >
                                    <div
                                      style={{
                                        background:
                                          item2.status === 'to_be_approved'
                                            ? '#ffef5f'
                                            : item2.color,
                                        height: 40,
                                        overflow: 'hidden',
                                        width: '50%',
                                        color: item2.text_color,
                                        borderRadius: '1px',
                                      }}
                                      className="has-text-centered pt-2"
                                      onMouseEnter={() => {
                                        setMouseHover({
                                          row: index2,
                                          column: item.id,
                                        });
                                      }}
                                      onMouseLeave={() => {
                                        setMouseHover(null);
                                      }}
                                    >
                                      {item2.code}
                                    </div>
                                  </Popover>
                                )}
                                <span className={'staff_leave_rest'} />
                              </div>
                            ),
                            rest_left: (
                              <div className="is-flex">
                                <span
                                  className={'staff_leave_rest'}
                                  style={{
                                    borderLeft: '1px solid #eff7ff',
                                  }}
                                />
                                {item2['type_name'] === null ? (
                                  <div
                                    style={{
                                      background:
                                        item2.status === 'to_be_approved'
                                          ? '#ffef5f'
                                          : item2.color === null && '#1E87A1',
                                      height: 40,
                                      width: '50%',
                                      borderRadius: '1px',
                                    }}
                                  />
                                ) : (
                                  <Popover
                                    content={absencePopoverContent}
                                    arrow={false}
                                    placement="bottomLeft"
                                  >
                                    <div
                                      style={{
                                        background:
                                          item2.status === 'to_be_approved'
                                            ? '#ffef5f'
                                            : item2.color,
                                        height: 40,
                                        width: '50%',
                                        color: item2.text_color,
                                        overflow: 'hidden',
                                        borderRadius: '1px',
                                      }}
                                      className="has-text-centered pt-2"
                                      onMouseEnter={() => {
                                        setMouseHover({
                                          row: index2,
                                          column: item.id,
                                        });
                                      }}
                                      onMouseLeave={() => {
                                        setMouseHover(null);
                                      }}
                                    >
                                      {item2.code}
                                    </div>
                                  </Popover>
                                )}
                              </div>
                            ),
                          }[item2['key']] ||
                            (allDayHoliday && <OffALL />) ||
                            (holidayLeft && (
                              <div
                                className="staff_leave_rest"
                                style={{
                                  borderLeft: '1px solid #eff7ff',
                                }}
                              />
                            )) ||
                            (holidayRight && (
                              <div
                                className="is-pulled-right staff_leave_rest"
                                style={{
                                  borderRight: '1px solid #eff7ff',
                                }}
                              />
                            )) ||
                            (restAll && <OffALL />) ||
                            (restLeft && (
                              <div
                                style={{
                                  borderLeft: '1px solid #eff7ff',
                                }}
                                className="staff_leave_rest"
                              />
                            )) ||
                            (restRight && (
                              <div
                                className="is-pulled-right staff_leave_rest"
                                style={{
                                  borderRight: '1px solid #eff7ff',
                                }}
                              />
                            ))}
                        </>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
        </tbody>
      </table>
      <div>
        <div className="is-flex is-justify-content-center is-align-content-center mt-2 staff_leave_footer">
          <AppPagination
            defaultCurrent={1}
            total={totalPage}
            onChange={onPageChange}
          />
        </div>
      </div>
      {/* FOOTER */}
      <StaffLeaveCalendarFooter
        absenceType={absenceType}
        isGetTobeApprove={isGetTobeApprove}
      />
    </LeaveAndAbsenceTable>
  );
};

const HeaderRow = styled.tr`
  border-collapse: separate;
`;
const LeaveAndAbsenceTable = styled.div`
  max-width: 93.75rem;
  margin: 0 auto;
  box-shadow: 0 2px 10px rgba(14, 48, 99, 0.25);
  border-radius: 10px;
  background: #fff;
  overflow-x: auto;
`;
const OffALL = styled.div`
  width: 100%;
  height: 100%;
  min-width: 40px;
  background: #c6c6c6;
  border-radius: 1px;
`;
export default StaffLeaveCalendarTables;
